import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import type { InboxItem } from '@/services/listingsService'
import { useLeadSearch } from '@/hooks/useLeadSearch'
import { useLeadView } from '@/store/leadView'
import { categoryLabel } from '@/lib/categoryLabel'

/**
 * CommandPalette — buscador GLOBAL (⌘K) tipo command palette.
 *
 * Se invoca desde el topbar (presente en Inicio/Búsqueda/Plan), así que funciona
 * en cualquier página. Dos secciones:
 *   · LEADS — busca con el motor compartido {@link useLeadSearch} (top 8). Al
 *     elegir uno deja el lead en el store global y navega a Búsqueda, que abre su
 *     ficha (LeadSlideOver).
 *   · IR A — navegación rápida (Inicio / Búsqueda / Mapa / Historial / Plan).
 *
 * Teclado: ↑↓ mueven el resaltado, Enter selecciona, Esc cierra. Clic en el
 * overlay cierra.
 */
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const

interface NavItem { label: string; hint: string; to: string; icon: ReactElement }

const NAV: NavItem[] = [
  { label: 'Inicio', hint: 'Panel principal', to: '/app', icon: (
    <svg viewBox="0 0 24 24" {...stroke}><path d="M3 11l9-8 9 8M5 10v10h14V10" /></svg>
  ) },
  { label: 'Búsqueda', hint: 'Bandeja de leads', to: '/app/busqueda', icon: (
    <svg viewBox="0 0 24 24" {...stroke}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
  ) },
  { label: 'Mapa', hint: 'Leads en el mapa', to: '/app/busqueda?view=map', icon: (
    <svg viewBox="0 0 24 24" {...stroke}><path d="M9 20l-5 2V6l5-2 6 2 5-2v16l-5 2-6-2z" /><path d="M9 4v16M15 6v16" /></svg>
  ) },
  { label: 'Historial', hint: 'Mis búsquedas', to: '/app/busqueda?view=history', icon: (
    <svg viewBox="0 0 24 24" {...stroke}><path d="M3 12a9 9 0 109-9 9 9 0 00-9 9z" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></svg>
  ) },
  { label: 'Plan', hint: 'Suscripción y cuota', to: '/app/plan', icon: (
    <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /></svg>
  ) },
]

function leadZone(it: InboxItem): string {
  return it.address_components?.city || it.address_components?.region || ''
}

export default function CommandPalette({
  open,
  onClose,
  initialQuery = '',
}: {
  open: boolean
  onClose: () => void
  initialQuery?: string
}) {
  const navigate = useNavigate()
  const requestLead = useLeadView((s) => s.requestLead)
  const [query, setQuery] = useState(initialQuery)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { results, loading } = useLeadSearch(query, { limit: 8, enabled: open })

  // Navegación: si hay query, solo las que coinciden; si no, todas.
  const navMatches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return NAV
    return NAV.filter((n) => n.label.toLowerCase().includes(q))
  }, [query])

  // Lista plana navegable por teclado: leads primero (intención principal al
  // buscar), navegación después.
  const rows = useMemo(
    () => [
      ...results.map((lead) => ({ kind: 'lead' as const, lead })),
      ...navMatches.map((nav) => ({ kind: 'nav' as const, nav })),
    ],
    [results, navMatches],
  )

  // Al abrir: resetear query, foco al input.
  useEffect(() => {
    if (!open) return
    setQuery(initialQuery)
    setActive(0)
    const id = setTimeout(() => inputRef.current?.focus(), 30)
    return () => clearTimeout(id)
  }, [open, initialQuery])

  // El resaltado nunca se sale de rango al cambiar los resultados.
  useEffect(() => { setActive((a) => (a >= rows.length ? 0 : a)) }, [rows.length])

  function selectRow(row: (typeof rows)[number] | undefined) {
    if (!row) return
    if (row.kind === 'nav') {
      navigate(row.nav.to)
      onClose()
      return
    }
    // Lead: lo deja en el store global y abre Búsqueda, que monta la ficha.
    requestLead(row.lead)
    navigate('/app/busqueda')
    onClose()
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (rows.length ? (a + 1) % rows.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (rows.length ? (a - 1 + rows.length) % rows.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectRow(rows[active])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  if (!open) return null

  const showLeads = results.length > 0 || (query.trim() !== '' && loading)
  let idx = -1 // índice global para mapear cada fila a su posición en `rows`

  return (
    <div className="cmdk-wrap" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cmdk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <svg className="cmdk-search-ic" viewBox="0 0 24 24" {...stroke}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input
            ref={inputRef}
            className="cmdk-input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0) }}
            onKeyDown={onKeyDown}
            placeholder="Buscar un lead o ir a… (nombre, categoría, ciudad)"
            aria-label="Buscar"
          />
          {loading && <span className="cmdk-spin" aria-hidden="true" />}
          <kbd className="cmdk-esc">esc</kbd>
        </div>

        <div className="cmdk-list">
          {showLeads && (
            <div className="cmdk-sec">
              <div className="cmdk-sec-h">Leads</div>
              {results.length === 0 && loading ? (
                <div className="cmdk-empty">Buscando…</div>
              ) : (
                results.map((lead) => {
                  idx++
                  const i = idx
                  const zone = leadZone(lead)
                  return (
                    <button
                      type="button"
                      key={lead.place_id}
                      className={`cmdk-item${active === i ? ' active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => selectRow({ kind: 'lead', lead })}
                    >
                      <span className="cmdk-it-ic">
                        <svg viewBox="0 0 24 24" {...stroke}><path d="M20 21a8 8 0 10-16 0" /><circle cx="12" cy="7" r="4" /></svg>
                      </span>
                      <span className="cmdk-it-main">
                        <span className="cmdk-it-name">{lead.title || '—'}</span>
                        <span className="cmdk-it-sub">
                          {categoryLabel(lead.category_primary)}{zone ? ` · ${zone}` : ''}
                        </span>
                      </span>
                      <span className="cmdk-it-go">Abrir ficha →</span>
                    </button>
                  )
                })
              )}
            </div>
          )}

          {query.trim() !== '' && !loading && results.length === 0 && (
            <div className="cmdk-empty">Sin leads para «{query.trim()}».</div>
          )}

          {navMatches.length > 0 && (
            <div className="cmdk-sec">
              <div className="cmdk-sec-h">Ir a</div>
              {navMatches.map((nav) => {
                idx++
                const i = idx
                return (
                  <button
                    type="button"
                    key={nav.to}
                    className={`cmdk-item${active === i ? ' active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => selectRow({ kind: 'nav', nav })}
                  >
                    <span className="cmdk-it-ic">{nav.icon}</span>
                    <span className="cmdk-it-main">
                      <span className="cmdk-it-name">{nav.label}</span>
                      <span className="cmdk-it-sub">{nav.hint}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> moverse</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  )
}
